<template>
  <Grid
    v-model="lm"
    :total="model.total_count"
    :columns="gridColumns"
    :data="model.data"
    :list-name="modalstack ? 'orders_related' : 'orders'"
    @click:row="onRowClick"
    :pagination="pagination"
    :persist="persist"
    :page-size-options="pageSizeOptions"
    :persist-key="persistKey"
  >
    <template #cell="{ row, col }">
      <template v-if="col.title == '_actions'">
        <div class="d-flex gap-1">
          <template v-if="modalstack">
            <RouterLinkStated
              active-class=""
              :to="{ name: 'orders.item', params: { id: row.id } }"
              target="_blank"
              class="btn btn-success btn-sm rounded-circle p-0 px-1"
            >
              <i class="bi bi-box-arrow-up-right"></i>
            </RouterLinkStated>
          </template>
        </div>
      </template>

      <template v-else-if="col.date && col.path">
        {{ commons.dateLocal(_result(row, col.path, row[col.title])) }}
      </template>

      <template v-else-if="col.date">
        {{ commons.dateLocal(col.path ? _result(row, col.path) : row[col.title]) }}
      </template>

      <template v-else-if="col.getter">
        {{ col.getter(row) }}
      </template>

      <template v-else>
        {{ pathLabel(row, col.path, row[col.title], locale) }}
      </template>
    </template>
  </Grid>
</template>

<script setup>
import { computed, inject, onMounted, provide, ref, shallowRef, watch } from 'vue'
import { result as _result } from 'lodash-es'
import { useRouter } from 'vue-router'
import { storeToRefs } from 'pinia'
import { commons } from '@/lib/transformer'
import { pathLabel, dropdownLabel } from '@/lib/dropdownLabel.js'
import { useApi } from '@/composables/api'
import { useConfigsStore } from '@/stores/configs'
import Grid from '@/components/list/grid.vue'
import RouterLinkStated from '@/components/RouterLink.vue'
import { useI18n } from 'vue-i18n'

const { t, locale } = useI18n()
const { config } = storeToRefs(useConfigsStore())
const router = useRouter()

const emits = defineEmits(['list-query', 'modalstack:close'])

function onRowClick(row) {
  if (props.modalstack) {
    emits('modalstack:close', row)
    return
  }
  router.push({ name: 'orders.item', params: { id: row.id } })
}
const props = defineProps({
  modalstack: {
    type: Boolean,
    default: false,
  },
  modelRef: {
    type: Object,
    default: null,
  },
  initial: {
    type: Object,
    default: () => ({}),
  },
  pagination: {
    type: Boolean,
    default: true,
  },
  persist: {
    type: Boolean,
    default: true,
  },
  pageSizeOptions: {
    type: Array,
    default: undefined,
  },
  persistKey: {
    type: String,
    default: '',
  },
  /** Модалка вкладки «Резерв»: колонки серии/кол-ва донора */
  reserveDonors: {
    type: Boolean,
    default: false,
  },
})

const model = inject('model')
const refresh = inject('refresh')
const includeArchived = inject('includeArchived')
const lm = ref(props.initial)

// При изменении чекбокса "Архивные" обновляем данные
watch(includeArchived, () => {
  refresh({
    query: {
      ...lm.value,
      include_archived: includeArchived.value || undefined,
    },
  })
})

const { model: users, refresh: fetchUsers } = useApi({
  url: 'users_list',
  query: { service: 'rtms' },
  data: [],
  pick: d => ({
    data: d.data ?? [],
    author: {
      assign_user: d.data.map(user => ({
        id: user.id,
        value: user.fio ? `${user.fio} (${user.username})` : user.username,
      })),
      'io_assign_user.username': true,
    },
    total_count: d.total_count ?? 0,
  }),
})

onMounted(fetchUsers)

// AS-1388: без immediate — иначе родитель стартовал запрос по начальному lm
// ещё до маунта List, а List на маунте накатывал сохранённые параметры
// пагинации вторым эмитом, отменяя первый запрос («orders (canceled)» в Network).
// List всегда вызывает modelValueSet на маунте, так что эмит гарантирован.
watch(lm, v => emits('list-query', v))
provide(
  'modalstack',
  computed(() => props.modalstack)
)
provide(
  'th-cfg',
  computed(() => ({
    ...config.value.orders,
    ...config.value.medicine_trademarks,
    ...users.value?.author,
  }))
)

const baseColumns = computed(() => [
  { title: 'id', label: t('orderNumber') },
  { title: 'name', label: t('medicineName'), path: 'medicine_trademark.name' },
  {
    title: 'medicinal_form',
    label: t('dosageForm'),
    path: 'medicine_trademark.medicinal_form.value',
  },
  { title: 'activity', label: t('activity'), path: 'activity.activity_subtype.parent_name' },
  { title: 'activity_subtype', label: t('activityType'), path: 'activity.activity_subtype.name' },
  { title: 'date_created', label: t('creationDate'), path: 'date_created', date: true },
  { title: 'laboratory.name', label: t('laboratory'), path: 'laboratory.name' },
  { title: 'status', label: t('status'), path: 'status.value', getter: row => {
    const id = row?.status?.id
    const fromCfg = (config.value?.orders?.status || []).find(s => s.id == id)
    return dropdownLabel(fromCfg || row?.status, locale.value)
  }},
  {
    title: 'assign_user',
    label: t('responsible'),
    path: 'assign_user.username',
    getter: o => o.assign_user?.fio || o.assign_user?.username,
  },
  {
    title: 'dispatch_date_expected',
    label: t('plannedSubmissionDate'),
    path: 'activity.shipment_date_scheduled',
    date: true,
  },
  {
    title: 'dispatch_date_executed',
    label: t('actualSubmissionDate'),
    path: 'dispatch_date_executed',
    date: true,
  },
])

/** Колонки серии для модалки выбора резерва (одна заявка — несколько серий). */
const reserveDonorColumns = [
  {
    title: 'reserve_serie',
    label: 'Серия',
    path: 'reserve_stock.serie',
    width: '120px',
  },
  {
    title: 'reserve_amount',
    label: 'В резерве',
    path: 'reserve_amount',
    width: '90px',
  },
  {
    title: 'reserve_lot',
    label: 'Номер лота',
    path: 'reserve_stock.vortex_id_number',
    width: '120px',
  },
]

/** Дефолтные ширины только для модалки «Связанные заявки» (list-name orders_related) */
const ordersRelatedDefaultWidths = {
  id: '60px',
  name: '200px',
  medicinal_form: '200px',
  activity: '90px',
  activity_subtype: '120px',
  date_created: '90px',
  'laboratory.name': '200px',
  status: '100px',
  assign_user: '130px',
  dispatch_date_expected: '100px',
  dispatch_date_executed: '100px',
  _actions: '100px',
}

const gridColumns = shallowRef([])
function updateGridColumns() {
  const cols = baseColumns.value.map(col => {
    const c = {...col}
    if(props.modalstack && ordersRelatedDefaultWidths[col.title] != null) {
      c.width = ordersRelatedDefaultWidths[col.title]
    }
    return c
  })

  if (props.modalstack) {
    if (props.reserveDonors) {
      // После № заявки — серия/кол-во донора (вкладка «Резерв»)
      cols.splice(1, 0, ...reserveDonorColumns)
    }
    cols.push({
      title: '_actions',
      label: t('actions'),
      static: true,
      width: ordersRelatedDefaultWidths._actions,
    })
  }
  gridColumns.value = cols
}
watch([() => props.modalstack, () => props.reserveDonors, locale], updateGridColumns, {
  immediate: true,
})
</script>

<i18n>
{
  "ru": {
    "orderNumber": "№ заявки",
    "medicineName": "Наименование ЛП",
    "dosageForm": "Лекарственная форма",
    "activity": "Активность",
    "activityType": "Тип активности",
    "creationDate": "Дата создания",
    "laboratory": "Лаборатория",
    "status": "Статус",
    "responsible": "Ответственный",
    "plannedSubmissionDate": "Планируемая дата подачи",
    "actualSubmissionDate": "Фактическая дата подачи",
    "actions": "Действия"
  },
  "en": {
    "orderNumber": "Order No.",
    "medicineName": "Medicine name",
    "dosageForm": "Dosage form",
    "activity": "Activity",
    "activityType": "Activity type",
    "creationDate": "Creation date",
    "laboratory": "Laboratory",
    "status": "Status",
    "responsible": "Responsible person",
    "plannedSubmissionDate": "Planned submission date",
    "actualSubmissionDate": "Actual submission date",
    "actions": "Actions"
  }
}
</i18n>
